import { Card, Chip, Link } from "@heroui/react";

import { AccountBadge } from "@/src/components/AccountBadge";
import { useLocale, useTranslations } from "@/src/i18n/i18n";
import { relativeTime } from "@/src/libs/relativeTime";
import { ConversationDialog } from "@/src/pages/Reports/components/ConversationDialog";
import { ReportActions } from "@/src/pages/Reports/components/ReportActions";
import { useReportedPostPhotos } from "@/src/pages/Reports/hooks/useReportedPostPhotos";
import {
  REASON_COLOR,
  REASON_KEY,
  STATUS_KEY,
  TARGET_KEY,
} from "@/src/pages/Reports/reportLabels";
import type { Report, ReportOutcome } from "@/src/pages/Reports/reports.type";
import { ROUTES } from "@/src/routes/paths";

type ReportCardProps = {
  report: Report;
  /** Vrai pendant que **ce** verdict part vers le serveur. */
  isResolving: boolean;
  /** Vrai pendant que **cette** suppression part vers le serveur. */
  isSanctioning: boolean;
  isSanctioned: boolean;
  onResolve: (_id: number, _outcome: ReportOutcome) => void;
  onSanction: (_report: Report) => void;
};

/**
 * Un signalement, et les deux choses qu'on peut en faire.
 *
 * La carte se lit dans l'ordre où la décision se prend : **ce qui est visé**
 * (le titre), **la pièce à juger** (la citation, l'élément le plus lisible de
 * la carte — c'est elle qu'on est venu lire), **d'où ça vient**, puis les
 * actions. Les règles du métier ne sont plus ici : elles sont dans l'aide de
 * l'écran (`ReportsHelp`), écrite une fois au lieu d'une fois par carte.
 *
 * ⚠️ `reportedContent` est une recopie faite au moment du signalement, pas un
 * lien vers le message. C'est voulu côté serveur : il reste consultable même si
 * l'auteur l'a supprimé depuis — et il le reste après une sanction.
 */
export function ReportCard({
  report,
  isResolving,
  isSanctioning,
  isSanctioned,
  onResolve,
  onSanction,
}: ReportCardProps) {
  const t = useTranslations("reports");
  const ta = useTranslations("a11y");
  const { locale } = useLocale();

  /*
    ⚠️ **Une galerie signalée n'a pas toujours de texte** : ce sont alors les
    photos qu'on juge. Le hook rend un tableau vide quand la cible n'est pas une
    publication, ou qu'elle n'est plus visible — les deux se traitent pareil,
    c'est la citation conservée qui prend le relais.
  */
  const photos = useReportedPostPhotos(
    report.targetType === "post" ? report.targetPostId : null,
  );

  // Recalculé à chaque rendu plutôt que figé dans un état : une file laissée
  // ouverte une heure afficherait sinon des âges d'il y a une heure.
  const age = relativeTime(report.createdAt, locale, Date.now());

  const reviewedAgo = report.reviewedAt
    ? relativeTime(report.reviewedAt, locale, Date.now())
    : null;

  return (
    <Card
      aria-busy={isResolving || isSanctioning}
      className="flex flex-col gap-4 p-5"
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        {/*
          `h2` et non un simple texte : la page est déjà titrée par la coque
          (`h1`), et une file se parcourt de titre en titre. « Message #128 »
          est aussi ce qu'on écrit dans un fil de discussion pour désigner
          celui-là plutôt qu'un autre.
        */}
        <h2 className="text-sm font-semibold tracking-wide uppercase">
          {t(TARGET_KEY[report.targetType])}{" "}
          <span className="text-muted">#{report.id}</span>
        </h2>

        <Chip color={REASON_COLOR[report.reason]} size="sm">
          {t(REASON_KEY[report.reason])}
        </Chip>

        {/*
          La fiche de la publication signalée : elle porte ce que cette carte ne
          peut pas montrer — le fil des commentaires, qui l'a aimée, les scores
          du classifieur. ⚠️ Elle reste atteignable même après une décision, la
          route d'administration voyant les quatre statuts ; seule une
          suppression **définitive** y mène désormais à « n'existe plus ».
        */}
        {report.targetType === "post" && report.targetPostId !== null ? (
          <Link className="text-xs" href={ROUTES.post(report.targetPostId)}>
            {t("open-post")}
          </Link>
        ) : null}

        {/* Le statut n'est une information que hors de la file en attente :
            dans « En attente », les six cartes porteraient la même puce. */}
        {report.status === "pending" ? null : (
          <Chip size="sm">{t(STATUS_KEY[report.status])}</Chip>
        )}

        {age ? (
          <time
            className="ml-auto text-xs text-muted"
            dateTime={report.createdAt}
          >
            {age}
          </time>
        ) : null}
      </div>

      {/*
        Un signalement de compte n'a pas de phrase à citer : ce qu'on juge,
        c'est **quelqu'un**. La carte disait donc « contenu non conservé », ce
        qui est vrai et inutile — et le bouton proposait de supprimer un compte
        sans le nommer. Le compte visé prend la place de la citation, sur le
        même fond : c'est le même emplacement, celui de la pièce à juger.
      */}
      {report.targetAccount === null ? null : (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-lg bg-default px-4 py-3 text-default-foreground">
          <span className="text-xs text-muted">
            {t("target-account-label")}
          </span>
          <span className="text-[15px]">
            <AccountBadge account={report.targetAccount} isLinked />
          </span>
        </div>
      )}

      {/*
        La pièce à juger, sur son propre fond et un cran plus grande que le
        reste : c'est le seul contenu de la carte qui demande d'être lu mot à
        mot. `bg-default` plutôt qu'une opacité choisie à la main — le jeton
        est déjà dosé pour les deux thèmes.
      */}
      {report.reportedContent ? (
        <blockquote className="rounded-lg bg-default px-4 py-3 text-[15px] text-pretty whitespace-pre-wrap text-default-foreground">
          {report.reportedContent}
        </blockquote>
      ) : report.targetAccount === null && photos.length === 0 ? (
        // ⚠️ « Contenu non conservé » ne se dit que si la carte n'a **rien**
        // d'autre à montrer : posé à côté des photos signalées, il affirmerait
        // le contraire de ce qu'on a sous les yeux.
        <p className="text-sm text-muted italic">{t("no-content")}</p>
      ) : null}

      {photos.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {/*
            ⚠️ **Un `alt` par photo, et numéroté.** Cinq images partageant le
            même libellé s'annoncent comme cinq fois la même ; le rang est la
            seule chose qu'on puisse en dire honnêtement, et c'est ce qui permet
            de nommer celle dont on parle.
          */}
          {photos.map((photo, rang) => (
            <img
              alt={ta("reported-photo", { position: rang + 1 })}
              className="size-24 rounded-md object-cover"
              key={photo.id}
              loading="lazy"
              src={photo.url}
            />
          ))}
        </div>
      ) : null}

      {report.description ? (
        <p className="text-sm text-pretty text-muted">{report.description}</p>
      ) : null}

      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 text-xs text-muted">
        {/*
          Un pseudo et une photo, pas un numéro de compte : c'est ce qui permet
          de reconnaître le signaleur qui revient tous les jours. Le signaleur
          est cliquable, les autres mentions non — c'est de lui qu'on veut
          ouvrir la fiche en cours de tri.
        */}
        <span className="flex min-w-0 items-center gap-1.5">
          {t("reported-by")}
          <AccountBadge account={report.reporter} isLinked />
        </span>

        {report.status === "pending" || report.reviewedBy === null ? null : (
          <span className="flex min-w-0 items-center gap-1.5">
            {t("reviewed-by")}
            <AccountBadge account={report.reviewedBy} />
            {reviewedAgo ? <span>· {reviewedAgo}</span> : null}
          </span>
        )}

        {/* Réservé aux signalements de message : l'endpoint de contexte répond
            404 `REPORT_CONTEXT_UNAVAILABLE` pour les autres types, autant ne
            pas proposer un bouton qui ne peut que décevoir. */}
        {report.targetType === "message" ? (
          <div className="ml-auto">
            <ConversationDialog reportId={report.id} />
          </div>
        ) : null}
      </div>

      <ReportActions
        isResolving={isResolving}
        isSanctioned={isSanctioned}
        isSanctioning={isSanctioning}
        onResolve={onResolve}
        onSanction={onSanction}
        report={report}
      />
    </Card>
  );
}
